import { useParams } from 'react-router-dom';
import type { EducationSession } from '@platform/billing';
import {
  useEducationCourse,
  useEducationSessions,
} from '../api/use-education.query';
import { useEducationCart } from '../model/education-cart.context';
import { SessionCard } from '../components/SessionCard';
import { CartBar } from '../components/CartBar';

/** 과정 상세 + 이 과정의 회차. 접수 종료된 회차도 보여준다(다음 기수 판단 근거). */
export function EducationCourseDetailView() {
  const { id } = useParams<{ id: string }>();
  const { data: course, isLoading, isError } = useEducationCourse(id);
  const { data: sessions } = useEducationSessions({
    courseId: id,
    limit: 50,
  });
  const cart = useEducationCart();

  const toggle = (session: EducationSession) =>
    cart.toggle({
      sessionId: session.id,
      courseId: session.courseId,
      courseTitle: session.courseTitle,
      sessionTitle: session.title,
      startAt: session.startAt,
      deliveryMode: session.deliveryMode,
      price: session.price,
    });

  if (isLoading)
    return (
      <p className="py-24 text-center text-sm text-gray-500">불러오는 중…</p>
    );
  if (isError || !course)
    return (
      <p className="py-24 text-center text-sm text-red-600">
        교육 과정을 불러오지 못했습니다.
      </p>
    );

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <header>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{course.code}</span>
        </div>
        <h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-neutral-100">
          {course.title}
        </h1>
        {course.summary && (
          <p className="mt-1 text-gray-500 dark:text-neutral-400">
            {course.summary}
          </p>
        )}
      </header>

      <dl className="mt-6 grid gap-3 rounded-xl border border-gray-200 p-4 text-sm sm:grid-cols-2 dark:border-white/15">
        {course.targetAudience && (
          <div>
            <dt className="text-gray-400">교육 대상</dt>
            <dd className="mt-0.5 text-gray-800 dark:text-neutral-200">
              {course.targetAudience}
            </dd>
          </div>
        )}
        {course.requirements && (
          <div>
            <dt className="text-gray-400">준비물</dt>
            <dd className="mt-0.5 text-gray-800 dark:text-neutral-200">
              {course.requirements}
            </dd>
          </div>
        )}
      </dl>

      {course.description && (
        <section className="mt-8">
          <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-neutral-100">
            과정 소개
          </h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-neutral-300">
            {course.description}
          </p>
        </section>
      )}

      {course.curriculum && (
        <section className="mt-8">
          <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-neutral-100">
            커리큘럼
          </h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-neutral-300">
            {course.curriculum}
          </p>
        </section>
      )}

      <section className="mt-8">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-neutral-100">
          회차
        </h2>
        {!sessions || sessions.items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500 dark:border-white/15">
            개설된 회차가 없습니다.
          </p>
        ) : (
          <ul className="space-y-3">
            {sessions.items.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                brandName={course.family}
                selected={cart.has(session.id)}
                onToggle={toggle}
              />
            ))}
          </ul>
        )}
      </section>

      <CartBar />
    </div>
  );
}
